import React, { useEffect, useState } from "react";
import { ReplyView } from "@/components/ReviewReply";
import { Link } from "react-router-dom";
import { MessageSquareQuote } from "lucide-react";
import { Stars } from "@/components/SpotReviews";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

export interface EventReview { review_id: string; event_id: string; venue_id?: string; event_title_ar?: string; author: string; rating: number; comment: string; created_at: string; venue_name_ar?: string; reply?: { text: string; by: string; at: string } | null }
export interface ReviewSummary { avg: number | null; count: number; items: EventReview[] }

export const RatingBadge = ({ s, testId }: { s: ReviewSummary; testId: string }) => {
  const { t } = usePrefs();
  if (!s.count) return null;
  return <span data-testid={testId} className="inline-flex items-center gap-1.5 text-sm font-bold"><Stars value={Math.round(s.avg || 0)} />{s.avg} · {t("rev.count").replace("{n}", String(s.count))}</span>;
};

export const ReviewItem = ({ r, showEvent, children }: { r: EventReview; showEvent?: boolean; children?: React.ReactNode }) => (
  <div data-testid={`event-review-${r.review_id}`} className="rounded-2xl bg-accent/60 p-4 space-y-1">
    <div className="flex items-center justify-between gap-2"><span className="font-bold text-sm">{r.author}</span><Stars value={r.rating} /></div>
    {showEvent && r.event_title_ar && <Link to={`/events/${r.event_id}`} className="block text-xs font-bold text-primary truncate">{r.event_title_ar}</Link>}
    {r.comment && <p className="text-sm flex gap-2"><MessageSquareQuote className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />{r.comment}</p>}
    <ReplyView r={r} />
    {children}
  </div>
);

export const EventReviews = ({ eventId, venueId }: { eventId?: string; venueId?: string }) => {
  const { t } = usePrefs();
  const [s, setS] = useState<ReviewSummary | null>(null);
  useEffect(() => {
    api.get(eventId ? `/events/${eventId}/reviews` : `/venues/${venueId}/reviews`).then((r) => setS(r.data)).catch(() => {});
  }, [eventId, venueId]);
  if (!s) return null;
  return (
    <section data-testid={eventId ? "event-reviews" : "venue-reviews"} className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="font-display text-lg font-bold">{t(eventId ? "er.title" : "er.venueTitle")}</h2>
        <RatingBadge s={s} testId={eventId ? "event-rating-summary" : "venue-rating-summary"} />
      </div>
      {s.count === 0 ? <p data-testid="event-reviews-empty" className="text-sm text-muted-foreground">{t("er.empty")}</p>
        : <div className="space-y-3">{s.items.slice(0, 10).map((r) => <ReviewItem key={r.review_id} r={r} showEvent={!eventId} />)}</div>}
    </section>
  );
};
